import PropTypes from 'prop-types'
import React from 'react'

import styled from '@emotion/styled'
import tokens from '../../design/design-tokens/tokens'

export const LINK_VARIANT_DEFAULT = 'default'
export const LINK_VARIANT_HIGHLIGHT = 'highlight'
export const LINK_VARIANT_EXTRALIGHT = 'extralight'
export const LINK_VARIANT_DANGER = 'danger'
export const LINK_VARIANT_DEFAULT_NEGATIVE = 'default-negative'
export const LINK_VARIANT_EXTRALIGHT_NEGATIVE = 'extralight-negative'

const StyledLink = styled.a`
  background: none;
  border: 0;
  box-shadow: none;

  transition: color 0.25s ease;

  &:hover {
    cursor: pointer;
  }

  ${({ variant, disabled }) => `
    ${handleVariant(variant)}
    ${disabled ? handleDisabledVariant(variant) : ''}
  `}

  ${({ $alignContent }) =>
    $alignContent ? `display: inline-flex; align-items: center;` : ''}

  ${({ $gap }) => ($gap !== undefined ? `gap: ${$gap}` : '')}
`

export const Link = ({
  children,
  className,
  variant,
  href,
  disabled,
  alignContent,
  gap,
  ...other
}) => {
  return (
    <StyledLink
      className={className}
      variant={variant}
      disabled={disabled}
      href={disabled ? undefined : href}
      $alignContent={alignContent}
      $gap={gap}
      {...other}
    >
      {children}
    </StyledLink>
  )
}

Link.defaultProps = {
  href: undefined,
  variant: undefined,
  disabled: false,
  alignContent: false
}

Link.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  href: PropTypes.string,
  disabled: PropTypes.bool,
  variant: PropTypes.oneOf([
    LINK_VARIANT_DEFAULT,
    LINK_VARIANT_HIGHLIGHT,
    LINK_VARIANT_EXTRALIGHT,
    LINK_VARIANT_DANGER,
    LINK_VARIANT_DEFAULT_NEGATIVE,
    LINK_VARIANT_EXTRALIGHT_NEGATIVE
  ]),
  alignContent: PropTypes.bool,
  gap: PropTypes.string
}

const handleVariant = variant => {
  switch (variant) {
    case LINK_VARIANT_DEFAULT:
      return `
        color: ${tokens.Link.default.textColor};
        text-decoration: ${tokens.Link.default.typography.textDecoration};
        font-weight: ${tokens.Link.default.typography.fontWeight};
        &:hover {
          color: ${tokens.Link.default_hover.textColor};
          text-decoration: ${tokens.Link.default_hover.typography.textDecoration};
          font-weight: ${tokens.Link.default_hover.typography.fontWeight};
        }
      `
    case LINK_VARIANT_HIGHLIGHT:
      return `
        font-weight: ${tokens.Link.highlight.typography.fontWeight};
        color: ${tokens.Link.highlight.textColor};
        text-decoration: ${tokens.Link.highlight.typography.textDecoration};
        &:hover {
          font-weight: ${tokens.Link.highlight_hover.typography.fontWeight};
          color: ${tokens.Link.highlight_hover.textColor};
          text-decoration: ${tokens.Link.highlight_hover.typography.textDecoration};
        }
      `
    case LINK_VARIANT_EXTRALIGHT:
      return `
        font-weight: ${tokens.Link.extralight.typography.fontWeight};
        color: ${tokens.Link.extralight.textColor};
        text-decoration: ${tokens.Link.extralight.typography.textDecoration};
        &:hover {
          font-weight: ${tokens.Link.extralight_hover.typography.fontWeight};
          color: ${tokens.Link.extralight_hover.textColor};
          text-decoration: ${tokens.Link.extralight_hover.typography.textDecoration};
        }
      `
    case LINK_VARIANT_DANGER:
      return `
        font-weight: ${tokens.Link.danger.typography.fontWeight};
        color: ${tokens.Link.danger.textColor};
        text-decoration: ${tokens.Link.danger.typography.textDecoration};
        &:hover {
          font-weight: ${tokens.Link.danger_hover.typography.fontWeight};
          color: ${tokens.Link.danger_hover.textColor};
          text-decoration: ${tokens.Link.danger_hover.typography.textDecoration};
        }
      `
    case LINK_VARIANT_DEFAULT_NEGATIVE:
      return `
        font-weight: ${tokens.Link.defaultNegative.typography.fontWeight};
        color: ${tokens.Link.defaultNegative.textColor};
        text-decoration: ${tokens.Link.defaultNegative.typography.textDecoration};
        &:hover {
          font-weight: ${tokens.Link.defaultNegative_hover.typography.fontWeight};
          color: ${tokens.Link.defaultNegative_hover.textColor};
          text-decoration: ${tokens.Link.defaultNegative_hover.typography.textDecoration};
        }
      `
    case LINK_VARIANT_EXTRALIGHT_NEGATIVE:
      return `
        font-weight: ${tokens.Link.extralightNegative.typography.fontWeight};
        color: ${tokens.Link.extralightNegative.textColor};
        text-decoration: ${tokens.Link.extralightNegative.typography.textDecoration};
        &:hover {
          font-weight: ${tokens.Link.extralightNegative_hover.typography.fontWeight};
          color: ${tokens.Link.extralightNegative_hover.textColor};
          text-decoration: ${tokens.Link.extralightNegative_hover.typography.textDecoration};
        }
      `
    default:
      return ''
  }
}

const handleDisabledVariant = variant => {
  switch (variant) {
    case LINK_VARIANT_DEFAULT:
      return `
          color: ${tokens.Link.default_disabled.textColor};
          text-decoration: ${tokens.Link.default_disabled.typography.textDecoration};
          font-weight: ${tokens.Link.default_disabled.typography.fontWeight};   
          cursor: ${tokens.Link.default_disabled.cursor}; 
          pointer-events: none;
          &:hover,
          &:focus,
          &:active {
            color: ${tokens.Link.default_disabled.textColor};
            text-decoration: ${tokens.Link.default_disabled.typography.textDecoration};
            font-weight: ${tokens.Link.default_disabled.typography.fontWeight};   
            cursor: ${tokens.Link.default_disabled.cursor}; 
            pointer-events: none;
          }
        `
    case LINK_VARIANT_HIGHLIGHT:
      return `
            color: ${tokens.Link.highlight_disabled.textColor};
            text-decoration: ${tokens.Link.highlight_disabled.typography.textDecoration};
            font-weight: ${tokens.Link.highlight_disabled.typography.fontWeight};
            cursor: ${tokens.Link.highlight_disabled.cursor}; 
            pointer-events: none;
            &:hover,
            &:focus,
            &:active {
              color: ${tokens.Link.highlight_disabled.textColor};
              text-decoration: ${tokens.Link.highlight_disabled.typography.textDecoration};
              font-weight: ${tokens.Link.highlight_disabled.typography.fontWeight};
              cursor: ${tokens.Link.highlight_disabled.cursor}; 
              pointer-events: none;
            }
          `
    case LINK_VARIANT_EXTRALIGHT:
      return `
            color: ${tokens.Link.extralight_disabled.textColor};
            text-decoration: ${tokens.Link.extralight_disabled.typography.textDecoration};
            font-weight: ${tokens.Link.extralight_disabled.typography.fontWeight};
            cursor: ${tokens.Link.extralight_disabled.cursor}; 
            pointer-events: none;
            &:hover,
            &:focus,
            &:active {
              color: ${tokens.Link.extralight_disabled.textColor};
              text-decoration: ${tokens.Link.extralight_disabled.typography.textDecoration};
              font-weight: ${tokens.Link.extralight_disabled.typography.fontWeight};
              cursor: ${tokens.Link.extralight_disabled.cursor}; 
              pointer-events: none;
            }
          `
    case LINK_VARIANT_DANGER:
      return `
            color: ${tokens.Link.danger_disabled.textColor};
            text-decoration: ${tokens.Link.danger_disabled.typography.textDecoration};
            font-weight: ${tokens.Link.danger_disabled.typography.fontWeight};
            cursor: ${tokens.Link.danger_disabled.cursor}; 
            pointer-events: none;
            &:hover,
            &:focus,
            &:active {
              color: ${tokens.Link.danger_disabled.textColor};
              text-decoration: ${tokens.Link.danger_disabled.typography.textDecoration};
              font-weight: ${tokens.Link.danger_disabled.typography.fontWeight};
              cursor: ${tokens.Link.danger_disabled.cursor}; 
              pointer-events: none;
            }
          `
    case LINK_VARIANT_DEFAULT_NEGATIVE:
      return `
            color: ${tokens.Link.defaultNegative_disabled.textColor};
            text-decoration: ${tokens.Link.defaultNegative_disabled.typography.textDecoration};
            font-weight: ${tokens.Link.defaultNegative_disabled.typography.fontWeight};
            cursor: ${tokens.Link.defaultNegative_disabled.cursor}; 
            pointer-events: none;
            &:hover,
            &:focus,
            &:active {
              color: ${tokens.Link.defaultNegative_disabled.textColor};
              text-decoration: ${tokens.Link.defaultNegative_disabled.typography.textDecoration};
              font-weight: ${tokens.Link.defaultNegative_disabled.typography.fontWeight};
              cursor: ${tokens.Link.defaultNegative_disabled.cursor}; 
              pointer-events: none;
            }
          `
    case LINK_VARIANT_EXTRALIGHT_NEGATIVE:
      return `
            color: ${tokens.Link.extralightNegative_disabled.textColor};
            text-decoration: ${tokens.Link.extralightNegative_disabled.typography.textDecoration};
            font-weight: ${tokens.Link.extralightNegative_disabled.typography.fontWeight};
            cursor: ${tokens.Link.extralightNegative_disabled.cursor}; 
            pointer-events: none;
            &:hover,
            &:focus,
            &:active {
              color: ${tokens.Link.extralightNegative_disabled.textColor};
              text-decoration: ${tokens.Link.extralightNegative_disabled.typography.textDecoration};
              font-weight: ${tokens.Link.extralightNegative_disabled.typography.fontWeight};
              cursor: ${tokens.Link.extralightNegative_disabled.cursor}; 
              pointer-events: none;
            }
          `
    default:
      return ''
  }
}
